import React from "react";
import { Phone, MessageCircle, ArrowRight, Shield, Clock, BadgeCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/lib/translations";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/4442d7a3-fb37-4f59-ba56-82f59fc70f4a/images/54c2b479920bf7044d8e3a2ead07ed50720645caa6fb1fed402065c0f47a7640.png";

const Hero = () => {
  const { t } = useLang();
  const icons = [Shield, Clock, BadgeCheck];

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[100vh] overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Faustitur luxury taxi on Algarve coast"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/55 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-40 pb-24 min-h-[100vh] flex flex-col justify-center">
        <div className="max-w-3xl">
          <p
            data-testid="hero-eyebrow"
            className="rise label-eyebrow inline-flex items-center gap-3"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--gold)] pulse-dot" />
            {t.hero.eyebrow}
          </p>

          <h1
            data-testid="hero-title"
            className="rise rise-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight text-white"
          >
            {t.hero.title.split(" ").slice(0, -2).join(" ")}{" "}
            <span className="italic text-[color:var(--gold)]">
              {t.hero.title.split(" ").slice(-2).join(" ")}
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="rise rise-delay-2 mt-6 text-base sm:text-lg text-zinc-300 leading-relaxed max-w-2xl"
          >
            {t.hero.subtitle}
          </p>

          <div className="rise rise-delay-3 mt-10 flex flex-wrap gap-4">
            <a href={COMPANY.telLink} data-testid="hero-call-btn" className="btn-gold">
              <Phone className="h-5 w-5" />
              {t.hero.callBtn}
            </a>
            <a
              href={`${COMPANY.whatsappLink}?text=${encodeURIComponent(
                "Olá Faustitur, gostaria de pedir uma reserva."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="btn-whatsapp"
            >
              <MessageCircle className="h-5 w-5" />
              {t.hero.whatsappBtn}
            </a>
            <a href="#booking" data-testid="hero-book-btn" className="btn-outline">
              {t.hero.bookBtn}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="rise rise-delay-3 mt-12 flex flex-wrap items-center gap-x-8 gap-y-3">
            {t.hero.badges.map((b, i) => {
              const Icon = icons[i] || Shield;
              return (
                <div
                  key={b}
                  data-testid={`hero-badge-${i}`}
                  className="flex items-center gap-2 text-zinc-300"
                >
                  <Icon className="h-4 w-4 text-[color:var(--gold)]" />
                  <span className="text-xs tracking-[0.2em] uppercase">{b}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Scroll hint */}
        <div className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 items-center gap-3 text-zinc-500 text-xs tracking-[0.3em] uppercase">
          <span className="gold-divider" /> Scroll <span className="gold-divider" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
